import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {IconId} from '@tabler/icons-react';
import React from 'react';
import {Button} from 'src/design/atoms/Button';

// CR80 landscape badge card size (3.375in x 2.125in) at 96 DPI — matches the
// `@page { size: 3.375in 2.125in }` rule the badge artifacts themselves
// declare. Kept local to this feature (rather than importing the equivalent
// constants from the Instances "Badge Templates" preview) to avoid coupling
// the Personnel module to an unrelated page module's internals; both are
// derived from the same physical card size.
export const BADGE_CARD_WIDTH_PX = 3.375 * 96; // 324
export const BADGE_CARD_HEIGHT_PX = 2.125 * 96; // 204

export interface BadgeArtifactPreviewProps {
  html: string | undefined;
  isLoading?: boolean;
  error?: string;
  title?: string;
  emptyMessage?: string;
  onPrint?: () => void;
  printDisabled?: boolean;
  hidePrintButton?: boolean;
}

// `sandbox=""` disables scripts, forms, and top-level navigation inside the
// iframe — a broken/hostile template cannot touch the app session.
export const BadgeArtifactPreview: React.FC<BadgeArtifactPreviewProps> = ({
  html,
  isLoading = false,
  error,
  title = 'Badge preview',
  emptyMessage = 'No badge has been generated yet.',
  onPrint,
  printDisabled = false,
  hidePrintButton = false,
}) => {
  return (
    <Stack gap="xs" data-testid="badge-artifact-preview">
      <Box
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 4,
          background: 'var(--mantine-color-gray-1)',
          width: BADGE_CARD_WIDTH_PX + 24,
          maxWidth: '100%',
          padding: 12,
        }}>
        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : error ? (
          <Text size="sm" c="red" ta="center" py="xl">
            {error}
          </Text>
        ) : html ? (
          <iframe
            title={title}
            data-testid="badge-artifact-preview-iframe"
            srcDoc={html}
            sandbox=""
            style={{
              width: BADGE_CARD_WIDTH_PX,
              maxWidth: '100%',
              height: BADGE_CARD_HEIGHT_PX,
              border: 'none',
              background: 'white',
              overflow: 'hidden',
            }}
          />
        ) : (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                <IconId size={24} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" ta="center">
                {emptyMessage}
              </Text>
            </Stack>
          </Center>
        )}
      </Box>

      {!hidePrintButton && (
        <Stack gap={4}>
          <Group>
            <Button
              size="sm"
              variant="default"
              disabled={!html || printDisabled}
              onClick={onPrint}>
              Print
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            Print at 100% / Actual Size — do not use Fit to Page.
          </Text>
        </Stack>
      )}
    </Stack>
  );
};
